import { useMutation } from '@tanstack/react-query'

import type { CsvFile } from '@/api/list'
import type { PeriodInfo } from '@/api/types'
import { formatInteger } from '@/design'
import { saveBlob } from '@/lib/download'
import { resolvePeriod, type GlobalFilters } from '@/lib/filters'
import './list.css'

/**
 * The whole matching list as CSV, generated by the API: the same filters and sort, every
 * page. Saved under the name the API gives it, else the one `filename` gives for the
 * period (the list's own, or else the filters'); says so when the file is cut at the
 * API's row limit. Its notices describe the last export of this query only: a page keys
 * it by its query, so a new query starts it afresh.
 */
export function CsvButton({
  download,
  filters,
  period,
  filename,
  noun,
  maxRows,
  hint,
}: {
  download: () => Promise<CsvFile>
  filters: GlobalFilters
  /** The period the list on screen covers; undefined while a previous list stands in for it. */
  period: PeriodInfo | undefined
  /** The API's own name for the file, which a fetched download loses. */
  filename: (from: string, to: string) => string
  /** The rows' plural, for the truncation note: "competitions". */
  noun: string
  /** The most rows one file carries. */
  maxRows: number
  /** A standing note about what the file holds. */
  hint?: string
}) {
  const csv = useMutation({
    mutationFn: download,
    onSuccess: (file) => {
      const { from, to } = period ?? resolvePeriod(filters, new Date()).current
      saveBlob(file.blob, file.filename ?? filename(from, to))
    },
  })
  const truncated = csv.data?.truncated ? csv.data : null
  return (
    <>
      <button type="button" className="ci-button" disabled={csv.isPending} onClick={() => csv.mutate()}>
        {csv.isPending ? 'Preparing CSV…' : 'Download CSV'}
      </button>
      {hint ? <span className="list-muted list-hint">{hint}</span> : null}
      {csv.isError ? (
        <p role="alert" className="list-error">
          The CSV could not be prepared just now. Try again in a moment.
        </p>
      ) : null}
      {truncated ? (
        <p role="status" className="list-note">
          The file holds the first {formatInteger(maxRows)}
          {truncated.totalRows !== null ? ` of ${formatInteger(truncated.totalRows)}` : ''} {noun}. Narrow the filters to
          export the rest.
        </p>
      ) : null}
    </>
  )
}
